import { useState, useRef, useCallback, useEffect } from 'react';
import { RoundProps } from './types';
import AdultHelp from './AdultHelp';

function StepDots({ total, current }: { total: number; current: number }) {
  return (
    <div className="flex justify-center gap-2 mb-3">
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} className={`rounded-full transition-all duration-300 ${
          i < current ? 'w-7 h-4 bg-green-400' : i === current ? 'w-7 h-4 bg-amber-400 animate-pulse' : 'w-4 h-4 bg-gray-200'
        }`} />
      ))}
    </div>
  );
}

function WrongWarning({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  useEffect(() => { const t = setTimeout(onDismiss, 2500); return () => clearTimeout(t); }, [onDismiss]);
  return (
    <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-red-500 text-white px-7 py-4 rounded-2xl text-lg font-bold shadow-lg animate-bounce max-w-sm text-center">
      🚫 {message}
    </div>
  );
}

function Instruction({ text, sub }: { text: string; sub?: string }) {
  return (
    <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl px-5 py-3 text-center w-full max-w-md mx-auto">
      <p className="text-base font-extrabold text-amber-800">{text}</p>
      {sub && <p className="text-sm text-amber-600 mt-1 italic">{sub}</p>}
    </div>
  );
}

// ══════════════════════════════════════════════════════
// CÔNG ĐOẠN 4: RỬA DỤNG CỤ VÀ ỐNG NỨA
// ══════════════════════════════════════════════════════
const DIRTY_ITEMS = [
  { id: 'tube', emoji: '🎋', label: 'Ống nứa', dirt: 100 },
  { id: 'leaf', emoji: '🍃', label: 'Lá chuối', dirt: 80 },
  { id: 'spoon', emoji: '🥄', label: 'Muỗng', dirt: 70 },
];

export function Round2RuaDungCu({ onComplete, bacteriaLevel, setBacteriaLevel, cleanLevel, setCleanLevel }: RoundProps) {
  const [step, setStep] = useState(0);
  const [brushInBasin, setBrushInBasin] = useState(false);
  const [draggingBrush, setDraggingBrush] = useState(false);
  const [draggingSoap, setDraggingSoap] = useState(false);
  const [currentItem, setCurrentItem] = useState(0);
  const [itemDirt, setItemDirt] = useState([100, 80, 70]);
  const [wrong, setWrong] = useState('');
  const [done, setDone] = useState(false);
  const scrubRef = useRef(false);
  const lastPosRef = useRef<{ x: number; y: number } | null>(null);
  const STEPS = ['Cho dụng cụ vào chậu', 'Đổ nước vào chậu', 'Kéo bàn chải vào', 'Kéo xà phòng vào', 'Giữ chuột và chà từng dụng cụ', 'Rửa lại bằng nước sạch', 'Để ráo nước'];
  const allClean = itemDirt.every(d => d <= 5);

  const handleScrubMouseDown = () => { if (step === 4) scrubRef.current = true; };
  const handleScrubMouseMove = useCallback((e: React.MouseEvent) => {
    if (!scrubRef.current || step !== 4) return;
    if (lastPosRef.current) {
      const dist = Math.sqrt(Math.pow(e.clientX - lastPosRef.current.x, 2) + Math.pow(e.clientY - lastPosRef.current.y, 2));
      setItemDirt(prev => {
        const next = [...prev];
        next[currentItem] = Math.max(0, next[currentItem] - dist * 0.5);
        if (next[currentItem] <= 0 && currentItem < DIRTY_ITEMS.length - 1) {
          setCurrentItem(c => c + 1);
        }
        return next;
      });
    }
    lastPosRef.current = { x: e.clientX, y: e.clientY };
  }, [step, currentItem]);
  const handleScrubMouseUp = () => {
    scrubRef.current = false;
    lastPosRef.current = null;
    if (allClean && step === 4) {
      setTimeout(() => setStep(5), 600);
    }
  };

  const advance = () => {
    const next = step + 1;
    if (next >= STEPS.length) {
      setDone(true);
      setCleanLevel(Math.min(100, cleanLevel + 25));
      setBacteriaLevel(Math.max(0, bacteriaLevel - 25));
      setTimeout(() => onComplete(true, 90), 800);
    } else setStep(next);
  };

  const handleSoapDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (step === 3) { setDraggingSoap(false); setTimeout(advance, 500); }
  };

  return (
    <div className="flex flex-col items-center gap-4 p-5 bg-amber-50 rounded-3xl min-h-96"
      onMouseMove={step === 4 ? handleScrubMouseMove : undefined}
      onMouseUp={step === 4 ? handleScrubMouseUp : undefined}>
      {wrong && <WrongWarning message={wrong} onDismiss={() => setWrong('')} />}
      <h3 className="text-2xl font-extrabold text-amber-700 text-center">🎋 Rửa dụng cụ và ống nứa!</h3>
      <StepDots total={STEPS.length} current={step} />

      {!done && (
        <div className="bg-white border-2 border-amber-200 rounded-2xl px-5 py-3 text-center w-full max-w-md">
          <p className="text-base font-extrabold text-amber-700">Bước {step + 1}: {STEPS[step]}</p>
        </div>
      )}

      <div className="flex gap-5 justify-center">
        {DIRTY_ITEMS.map((item, idx) => (
          <div key={item.id} className="relative flex flex-col items-center gap-2">
            <span className="text-4xl">{item.emoji}</span>
            <div className="w-16 h-3 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-amber-400 rounded-full transition-all"
                style={{ width: `${itemDirt[idx]}%` }} />
            </div>
            <span className="text-sm font-bold text-amber-600">{item.label}</span>
            {idx === currentItem && step === 4 && <span className="text-sm text-red-500 font-extrabold animate-pulse">← Chà đây!</span>}
            {itemDirt[idx] <= 5 && <span className="text-sm text-green-600 font-extrabold">✅ Sạch!</span>}
          </div>
        ))}
      </div>

      {step === 0 && <button onClick={advance} className="px-8 py-4 bg-amber-500 text-white font-extrabold text-base rounded-2xl cursor-pointer hover:bg-amber-600 whitespace-nowrap">🪣 Cho dụng cụ vào chậu!</button>}
      {step === 1 && <button onClick={advance} className="px-8 py-4 bg-blue-500 text-white font-extrabold text-base rounded-2xl cursor-pointer hover:bg-blue-600 whitespace-nowrap animate-pulse">💧 Đổ nước vào chậu!</button>}

      {step === 2 && (
        <div className="flex items-center gap-6">
          <div draggable onDragStart={() => setDraggingBrush(true)} onDragEnd={() => setDraggingBrush(false)}
            className={`flex flex-col items-center gap-2 cursor-grab active:cursor-grabbing select-none transition-all ${draggingBrush ? 'opacity-40' : 'hover:scale-110 animate-bounce'}`}>
            <span className="text-5xl">🪥</span>
            <span className="text-sm font-extrabold text-amber-600">Kéo bàn chải →</span>
          </div>
          <div onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); setBrushInBasin(true); setDraggingBrush(false); setTimeout(advance, 500); }}
            className="w-20 h-20 rounded-xl border-4 border-dashed border-amber-400 flex items-center justify-center bg-amber-50 text-3xl">
            🪣
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="flex items-center gap-6">
          <div draggable onDragStart={() => setDraggingSoap(true)} onDragEnd={() => setDraggingSoap(false)}
            className={`flex flex-col items-center gap-2 cursor-grab active:cursor-grabbing select-none transition-all ${draggingSoap ? 'opacity-40' : 'hover:scale-110 animate-bounce'}`}>
            <span className="text-5xl">🧼</span>
            <span className="text-sm font-extrabold text-yellow-600">Kéo xà phòng →</span>
          </div>
          <div onDragOver={e => e.preventDefault()} onDrop={handleSoapDrop}
            className="w-20 h-20 rounded-xl border-4 border-dashed border-yellow-400 flex items-center justify-center text-3xl bg-yellow-50">
            🪣
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="flex flex-col items-center gap-3 w-full max-w-sm">
          <Instruction text="🖱️ Giữ chuột và di chuyển lên xuống để chà sạch từng vật!" sub="Chà cho đến khi vết bẩn biến mất hoàn toàn" />
          <div
            onMouseDown={handleScrubMouseDown}
            className="w-full h-24 rounded-2xl bg-gradient-to-b from-amber-100 to-amber-200 border-4 border-amber-400 flex items-center justify-center cursor-ns-resize select-none text-4xl font-bold transition-all hover:border-amber-600 active:bg-amber-300">
            {brushInBasin ? '🪥' : '🤲'} <span className="text-base ml-2">Giữ và chà mạnh tay!</span>
          </div>
          <div className="flex items-center gap-3 text-sm text-amber-600 font-bold">
            <span>Đang chà: {DIRTY_ITEMS[currentItem]?.label}</span>
            <span>{currentItem + 1}/{DIRTY_ITEMS.length}</span>
          </div>
          {allClean && <p className="text-green-600 font-extrabold text-base">✅ Tất cả sạch sẽ rồi!</p>}
        </div>
      )}

      {step === 5 && <button onClick={advance} className="px-8 py-4 bg-sky-500 text-white font-extrabold text-base rounded-2xl cursor-pointer hover:bg-sky-600 whitespace-nowrap animate-pulse">💦 Rửa lại bằng nước sạch!</button>}
      {step === 6 && <button onClick={advance} className="px-8 py-4 bg-green-500 text-white font-extrabold text-base rounded-2xl cursor-pointer hover:bg-green-600 whitespace-nowrap">🫙 Để ráo nước xong!</button>}
      {done && <p className="text-xl font-extrabold text-green-600 text-center">✅ Dụng cụ sạch bóng! Vi khuẩn đã biến mất!</p>}
    </div>
  );
}

// ── Water Drop Effect ─────────────────────────────────────────────────
function _WaterDrops({ active }: { active: boolean }) {
  if (!active) return null;
  return (
    <div className="absolute -top-8 left-1/2 -translate-x-1/2 flex gap-1 z-20 pointer-events-none">
      {[0, 1, 2, 3].map(i => (
        <div key={i} className="text-blue-400 text-xl animate-bounce"
          style={{ animationDelay: `${i * 0.1}s`, animationDuration: '0.4s' }}>💧</div>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════
// CÔNG ĐOẠN 5: VO GẠO
// ══════════════════════════════════════════════════════
export function Round3VoGao({ onComplete, cleanLevel, setCleanLevel }: RoundProps) {
  const [step, setStep] = useState(0);
  const [circleProgress, setCircleProgress] = useState(0);
  const [circle2Progress, setCircle2Progress] = useState(0);
  const [draggingBowl, setDraggingBowl] = useState(false);
  const [draggingWater, setDraggingWater] = useState(false);
  const [done, setDone] = useState(false);
  const [waterPouring, setWaterPouring] = useState(false);
  const [waterPouring2, setWaterPouring2] = useState(false);
  const [waterPouringOut, setWaterPouringOut] = useState(false);
  const scrubRef = useRef(false);
  const angleRef = useRef<number | null>(null);
  const centerRef = useRef<{ x: number; y: number } | null>(null);
  const bowlRef = useRef<HTMLDivElement>(null);
  const CIRCLE_TARGET = 200;

  const STEPS = ['Đổ gạo vào thau', 'Đổ nước vào thau', 'Giữ và xoay tròn để vo gạo', 'Kéo thau để đổ nước bẩn đi', 'Kéo nước sạch vào thau', 'Xoay tròn lần 2 cho đến khi nước trong'];

  const advance = useCallback(() => {
    const next = step + 1;
    if (next >= STEPS.length) {
      setDone(true);
      setCleanLevel(Math.min(100, cleanLevel + 20));
      setTimeout(() => onComplete(true, 90), 800);
    } else setStep(next);
  }, [step, cleanLevel, setCleanLevel, onComplete]);

  const handleBowlMouseDown = useCallback((e: React.MouseEvent) => {
    if (![2, 5].includes(step)) return;
    scrubRef.current = true;
    if (bowlRef.current) {
      const rect = bowlRef.current.getBoundingClientRect();
      centerRef.current = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
    }
    const dx = e.clientX - (centerRef.current?.x ?? e.clientX);
    const dy = e.clientY - (centerRef.current?.y ?? e.clientY);
    angleRef.current = Math.atan2(dy, dx);
  }, [step]);

  const handleBowlMouseMove = useCallback((e: React.MouseEvent) => {
    if (!scrubRef.current || !centerRef.current || angleRef.current === null) return;
    if (![2, 5].includes(step)) return;
    const dx = e.clientX - centerRef.current.x;
    const dy = e.clientY - centerRef.current.y;
    const newAngle = Math.atan2(dy, dx);
    let delta = newAngle - angleRef.current;
    if (delta > Math.PI) delta -= 2 * Math.PI;
    if (delta < -Math.PI) delta += 2 * Math.PI;
    const dist = Math.abs(delta) * 50;
    if (step === 2) {
      setCircleProgress(v => {
        const next = Math.min(CIRCLE_TARGET, v + dist);
        if (next >= CIRCLE_TARGET) { scrubRef.current = false; setTimeout(advance, 500); }
        return next;
      });
    } else {
      setCircle2Progress(v => {
        const next = Math.min(CIRCLE_TARGET, v + dist);
        if (next >= CIRCLE_TARGET) { scrubRef.current = false; setTimeout(advance, 500); }
        return next;
      });
    }
    angleRef.current = newAngle;
  }, [step, advance]);

  const handleBowlMouseUp = () => { scrubRef.current = false; };

  const waterColor = step >= 4
    ? `rgba(${147 + Math.round((1 - (1 - circle2Progress / CIRCLE_TARGET)) * 70)}, 210, 253, 0.5)`
    : step >= 1 ? `rgba(180, 200, 220, 0.5)` : 'transparent';

  return (
    <div className="flex flex-col items-center gap-3 p-4 bg-blue-50 rounded-3xl"
      onMouseMove={[2, 5].includes(step) ? handleBowlMouseMove : undefined}
      onMouseUp={[2, 5].includes(step) ? handleBowlMouseUp : undefined}>
      <h3 className="text-xl font-extrabold text-blue-700 text-center">🍚 Vo gạo sạch!</h3>
      <div className="flex justify-center gap-1.5 mb-1">
        {Array.from({ length: STEPS.length }).map((_, i) => (
          <div key={i} className={`rounded-full transition-all duration-300 ${
            i < step ? 'w-7 h-4 bg-green-400' : i === step ? 'w-7 h-4 bg-amber-400 animate-pulse' : 'w-4 h-4 bg-gray-200'
          }`} />
        ))}
      </div>
      {!done && (
        <div className="bg-white border-2 border-blue-200 rounded-2xl px-4 py-2 text-center">
          <p className="text-sm font-extrabold text-blue-700">Bước {step + 1}: {STEPS[step]}</p>
        </div>
      )}

      <div className="relative">
        {(waterPouring || waterPouring2) && (
          <div className="absolute -top-8 left-1/2 -translate-x-1/2 flex gap-1 z-20 pointer-events-none">
            {[0, 1, 2, 3].map(i => (
              <div key={i} className="text-blue-400 text-xl animate-bounce"
                style={{ animationDelay: `${i * 0.1}s`, animationDuration: '0.4s' }}>💧</div>
            ))}
          </div>
        )}
        {waterPouringOut && (
          <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 flex gap-1 z-20 pointer-events-none">
            {[0, 1, 2, 3].map(i => (
              <div key={i} className="text-amber-400 text-xl animate-bounce"
                style={{ animationDelay: `${i * 0.1}s`, animationDuration: '0.3s' }}>💦</div>
            ))}
          </div>
        )}
        <div
          ref={bowlRef}
          onMouseDown={[2, 5].includes(step) ? handleBowlMouseDown : undefined}
          className={`relative w-44 h-28 rounded-b-3xl border-4 overflow-hidden transition-all ${
            [2, 5].includes(step) ? 'border-blue-400 cursor-crosshair hover:border-blue-600' : 'border-blue-300'
          } ${draggingBowl ? 'rotate-45 scale-90' : ''}`}
          style={{ background: step >= 1 ? waterColor : '#f3f4f6' }}>
          {step >= 0 && (
            <div className="absolute bottom-0 left-0 right-0 h-8 flex items-center justify-center">
              <span className="text-3xl">🍚</span>
            </div>
          )}
          {[2, 5].includes(step) && (
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-xs font-extrabold text-blue-600 bg-white/70 px-2 py-1 rounded-lg">Giữ + xoay tròn</span>
            </div>
          )}
          {[2, 5].includes(step) && (
            <div className="absolute top-1 right-1 text-xl font-bold text-blue-500 animate-spin" style={{ animationDuration: '2s' }}>↻</div>
          )}
        </div>
      </div>

      {(step === 2 || step === 5) && (
        <div className="flex items-center gap-2 text-xs font-bold">
          <div className="w-4 h-4 rounded-full bg-amber-400" />
          <span className="text-gray-500">Đục</span>
          <div className="w-24 h-3 rounded-full overflow-hidden bg-gray-200">
            <div className="h-full bg-gradient-to-r from-amber-300 to-sky-300 rounded-full transition-all"
              style={{ width: `${step === 2 ? (circleProgress / CIRCLE_TARGET) * 100 : (circle2Progress / CIRCLE_TARGET) * 100}%` }} />
          </div>
          <span className="text-sky-400">Trong</span>
          <div className="w-4 h-4 rounded-full bg-sky-200" />
        </div>
      )}

      {step === 0 && <button onClick={advance} className="px-6 py-3 bg-amber-500 text-white font-extrabold text-sm rounded-2xl cursor-pointer hover:bg-amber-600 whitespace-nowrap">🍚 Đổ gạo vào thau!</button>}
      {step === 1 && (
        <button onClick={() => {
          setWaterPouring(true);
          setTimeout(() => setWaterPouring(false), 1200);
          setTimeout(advance, 800);
        }} className="px-6 py-3 bg-blue-500 text-white font-extrabold text-sm rounded-2xl cursor-pointer hover:bg-blue-600 whitespace-nowrap animate-pulse">💧 Đổ nước vào!</button>
      )}

      {step === 2 && (
        <div className="flex flex-col items-center gap-2">
          <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl px-4 py-2 text-center w-full max-w-md">
            <p className="text-xs font-extrabold text-amber-800">🖱️ Giữ chuột trên thau và xoay theo vòng tròn để vo gạo!</p>
          </div>
          <div className="w-full max-w-sm h-3 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full bg-blue-400 rounded-full transition-all" style={{ width: `${(circleProgress / CIRCLE_TARGET) * 100}%` }} />
          </div>
          <p className="text-xs text-blue-600 font-bold">Vo gạo: {Math.round((circleProgress / CIRCLE_TARGET) * 100)}%</p>
        </div>
      )}

      {step === 3 && (
        <div className="flex flex-col items-center gap-3">
          <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl px-4 py-2 text-center">
            <p className="text-xs font-extrabold text-amber-800">👆 Kéo thau nghiêng để đổ nước bẩn đi!</p>
          </div>
          <div draggable
            onDragStart={() => { setDraggingBowl(true); setWaterPouringOut(true); }}
            onDragEnd={() => { setDraggingBowl(false); setWaterPouringOut(false); }}
            className={`flex flex-col items-center gap-1 cursor-grab active:cursor-grabbing select-none transition-all ${draggingBowl ? 'opacity-40 rotate-45' : 'hover:scale-110 animate-bounce'}`}>
            <span className="text-4xl">🪣</span>
            <span className="text-xs text-orange-600 font-extrabold">Kéo để đổ đi →</span>
          </div>
          <div onDragOver={e => e.preventDefault()} onDrop={e => {
            e.preventDefault(); setDraggingBowl(false);
            setWaterPouringOut(true);
            setTimeout(() => setWaterPouringOut(false), 800);
            setTimeout(advance, 400);
          }}
            className="w-full h-12 rounded-2xl border-2 border-dashed border-orange-400 flex items-center justify-center text-sm text-orange-500 font-extrabold bg-orange-50">
            💦 Thả vào đây để đổ nước đi
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="flex items-center gap-5">
          <div draggable onDragStart={() => setDraggingWater(true)} onDragEnd={() => setDraggingWater(false)}
            className={`flex flex-col items-center gap-1 cursor-grab active:cursor-grabbing select-none transition-all ${draggingWater ? 'opacity-40' : 'hover:scale-110 animate-bounce'}`}>
            <span className="text-5xl">🚿</span>
            <span className="text-xs font-extrabold text-blue-600">Kéo nước sạch →</span>
          </div>
          <div onDragOver={e => e.preventDefault()} onDrop={e => {
            e.preventDefault(); setDraggingWater(false);
            setWaterPouring2(true);
            setTimeout(() => setWaterPouring2(false), 1000);
            setTimeout(advance, 700);
          }}
            className="w-16 h-16 rounded-xl border-4 border-dashed border-blue-400 flex items-center justify-center text-2xl bg-blue-50">
            🍚
          </div>
        </div>
      )}

      {step === 5 && (
        <div className="flex flex-col items-center gap-2">
          <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl px-4 py-2 text-center">
            <p className="text-xs font-extrabold text-amber-800">🖱️ Xoay tròn lần nữa cho đến khi nước trong!</p>
          </div>
          <div className="w-full max-w-sm h-3 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full bg-sky-300 rounded-full transition-all" style={{ width: `${(circle2Progress / CIRCLE_TARGET) * 100}%` }} />
          </div>
          <p className="text-xs text-blue-600 font-bold">Vo lần 2: {Math.round((circle2Progress / CIRCLE_TARGET) * 100)}%</p>
        </div>
      )}

      {done && <p className="text-base font-extrabold text-green-600 text-center">✅ Gạo đã vo sạch! Nước trong vắt!</p>}
    </div>
  );
}

// ══════════════════════════════════════════════════════
// CÔNG ĐOẠN 6: CHUẨN BỊ ỐNG NỨA
// ══════════════════════════════════════════════════════
export function Round4ChuanBiOng({ onComplete }: RoundProps) {
  const [step, setStep] = useState(0);
  const [riceAmount, setRiceAmount] = useState(0);
  const [waterLevel, setWaterLevel] = useState(0);
  const [draggingRice, setDraggingRice] = useState(false);
  const [cupTiltProgress, setCupTiltProgress] = useState(0);
  const [draggingLeaf, setDraggingLeaf] = useState(false);
  const [leafPlaced, setLeafPlaced] = useState(false);
  const [leafOffset, setLeafOffset] = useState(0);
  const [pressProgress, setPressProgress] = useState(0);
  const [tieClicks, setTieClicks] = useState(0);
  const [adultDone, setAdultDone] = useState(false);
  const [done, setDone] = useState(false);
  const [wrong, setWrong] = useState('');
  const [waterDrops, setWaterDrops] = useState(false);
  const cupScrubRef = useRef(false);
  const pressRef = useRef(false);
  const lastPosRef = useRef<{ x: number; y: number } | null>(null);
  const STEPS = ['Rửa sạch ống nứa', 'Lau khô ống nứa', 'Kéo gạo vào ống', 'Đong nước vào ống', 'Kéo thả lá lên miệng ống', 'Giữ và xoay ép lá bám chặt', 'Bấm nhiều lần buộc dây cố định lá'];
  const TIE_TARGET = 7;
  const PRESS_TARGET = 100;

  function WrongHint({ message, onDismiss }: { message: string; onDismiss: () => void }) {
    useEffect(() => { const t = setTimeout(onDismiss, 2500); return () => clearTimeout(t); }, [onDismiss]);
    return (
      <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-red-500 text-white px-7 py-4 rounded-2xl text-lg font-bold shadow-lg animate-bounce max-w-sm text-center">
        🚫 {message}
      </div>
    );
  }

  const advance = useCallback(() => {
    const next = step + 1;
    setStep(next);
  }, [step]);

  const handleRiceDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDraggingRice(false);
    const newAmt = Math.min(67, riceAmount + 22);
    setRiceAmount(newAmt);
    if (newAmt >= 60) setTimeout(advance, 600);
  };

  const handleCupMouseDown = () => { if (step === 3) cupScrubRef.current = true; };
  const handleCupMouseMove = useCallback((e: React.MouseEvent) => {
    if (!cupScrubRef.current || step !== 3) return;
    if (lastPosRef.current) {
      const dy = lastPosRef.current.y - e.clientY;
      setCupTiltProgress(v => Math.max(0, Math.min(100, v + dy * 0.3)));
      setWaterLevel(v => {
        const next = Math.min(80, v + Math.max(0, dy * 0.2));
        if (next >= 2) {
          setWaterDrops(true);
          setTimeout(() => setWaterDrops(false), 600);
        }
        if (next >= 75) {
          cupScrubRef.current = false;
          setTimeout(advance, 600);
        }
        return next;
      });
    }
    lastPosRef.current = { x: e.clientX, y: e.clientY };
  }, [step, advance]);
  const handleCupMouseUp = () => { cupScrubRef.current = false; lastPosRef.current = null; };

  const handlePressMouseDown = () => { if (step === 5) pressRef.current = true; };
  const handlePressMouseMove = useCallback((e: React.MouseEvent) => {
    if (!pressRef.current || step !== 5) return;
    if (lastPosRef.current) {
      const dist = Math.sqrt(Math.pow(e.clientX - lastPosRef.current.x, 2) + Math.pow(e.clientY - lastPosRef.current.y, 2));
      setPressProgress(v => {
        const next = Math.min(PRESS_TARGET, v + dist * 0.8);
        if (next >= PRESS_TARGET) {
          pressRef.current = false;
          if (Math.abs(leafOffset) > 8) setWrong('Lá đặt lệch! Cơm có thể trào ra khi nấu!');
          setTimeout(advance, 600);
        }
        return next;
      });
    }
    lastPosRef.current = { x: e.clientX, y: e.clientY };
  }, [step, leafOffset, advance]);
  const handlePressMouseUp = () => { pressRef.current = false; lastPosRef.current = null; };

  const handleLeafDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDraggingLeaf(false);
    setLeafOffset((Math.random() - 0.5) * 20);
    setLeafPlaced(true);
    setTimeout(advance, 500);
  };

  const handleTieClick = () => {
    if (step !== 6) return;
    setTieClicks(v => {
      const next = v + 1;
      if (next >= TIE_TARGET) setTimeout(advance, 400);
      return next;
    });
  };

  const isAtAdultStep = step >= STEPS.length;

  return (
    <div className="flex flex-col items-center gap-4 p-5 bg-amber-50 rounded-3xl min-h-96"
      onMouseMove={step === 3 ? handleCupMouseMove : step === 5 ? handlePressMouseMove : undefined}
      onMouseUp={step === 3 ? handleCupMouseUp : step === 5 ? handlePressMouseUp : undefined}>
      {wrong && <WrongHint message={wrong} onDismiss={() => setWrong('')} />}
      <h3 className="text-2xl font-extrabold text-amber-700 text-center">🎍 Chuẩn bị ống nứa!</h3>
      <StepDots total={STEPS.length + 1} current={step} />

      {step < STEPS.length && (
        <div className="bg-white border-2 border-amber-200 rounded-2xl px-5 py-3 text-center w-full max-w-md">
          <p className="text-base font-extrabold text-amber-700">Bước {step + 1}: {STEPS[step]}</p>
        </div>
      )}

      <div className="flex items-start gap-6 justify-center">
        <div
          onDragOver={step === 2 ? e => e.preventDefault() : undefined}
          onDrop={step === 2 ? handleRiceDrop : undefined}
          className={`relative w-16 h-52 rounded-2xl border-4 flex flex-col-reverse overflow-visible cursor-pointer transition-all select-none ${step >= 1 ? 'border-amber-400' : 'border-gray-300'}`}
          style={{ background: step >= 3 ? 'linear-gradient(to top, #fde68a 70%, rgba(147,197,253,0.3) 100%)' : '#fff' }}>
          <div className="transition-all duration-500 bg-amber-200 rounded-b-xl" style={{ height: `${riceAmount}%` }} />
          {step >= 3 && (
            <>
              {/* Water drops animation */}
              {waterDrops && (
                <div className="absolute -top-8 left-1/2 -translate-x-1/2 flex gap-0.5 pointer-events-none z-20">
                  {[0,1,2].map(i => (
                    <div key={i} className="text-blue-400 text-sm animate-bounce" style={{ animationDelay: `${i*0.1}s`, animationDuration: '0.3s' }}>💧</div>
                  ))}
                </div>
              )}
              <div className="absolute left-0 right-0 bg-blue-300 opacity-60 transition-all" style={{ bottom: `${riceAmount}%`, height: `${waterLevel * 0.4}%` }} />
            </>
          )}
          {step === 3 && (
            <>
              <div className="absolute right-0 w-3 h-0.5 bg-green-600" style={{ bottom: '50%' }} />
              <div className="absolute -right-10 text-sm text-green-600 font-bold whitespace-nowrap" style={{ bottom: '49%' }}>← vừa</div>
            </>
          )}
          {step >= 4 && leafPlaced && (
            <div className="absolute -top-2 -left-2 -right-2 h-7 bg-green-400 rounded-xl flex items-center justify-center text-lg border-2 border-green-600"
              style={{ transform: `translateX(${leafOffset}px)` }}>
              🍃
            </div>
          )}
          {step >= 6 && tieClicks > 0 && (
            <div className="absolute top-2 left-0 right-0 h-2 border-2 border-amber-700 rounded-full"
              style={{ opacity: tieClicks / TIE_TARGET }} />
          )}
          {adultDone && <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-gray-700 border-2 border-gray-500" />}
        </div>

        {step === 2 && (
          <div draggable onDragStart={() => setDraggingRice(true)} onDragEnd={() => setDraggingRice(false)}
            className={`flex flex-col items-center gap-2 cursor-grab active:cursor-grabbing select-none transition-all mt-8 ${draggingRice ? 'opacity-40' : 'hover:scale-110 animate-bounce'}`}>
            <span className="text-5xl">🍚</span>
            <span className="text-sm font-extrabold text-amber-600">← Kéo vào ống</span>
            <span className="text-sm text-amber-500">{riceAmount}%/67%</span>
          </div>
        )}

        {step === 3 && (
          <div
            onMouseDown={handleCupMouseDown}
            className="flex flex-col items-center gap-2 mt-8 select-none cursor-n-resize transition-all hover:scale-110"
            style={{ transform: `rotate(${cupTiltProgress * 0.8}deg)` }}>
            <span className="text-6xl">🫗</span>
            <span className="text-sm font-extrabold text-blue-600">Giữ + kéo lên</span>
            <span className="text-sm text-blue-500">để đổ nước</span>
          </div>
        )}

        {step === 4 && !leafPlaced && (
          <div draggable onDragStart={() => setDraggingLeaf(true)} onDragEnd={() => setDraggingLeaf(false)}
            className={`flex flex-col items-center gap-2 mt-4 cursor-grab active:cursor-grabbing select-none transition-all ${draggingLeaf ? 'opacity-40' : 'hover:scale-110 animate-bounce'}`}>
            <span className="text-6xl">🍃</span>
            <span className="text-sm font-extrabold text-green-600">← Kéo lá vào miệng ống</span>
          </div>
        )}
      </div>

      {step === 0 && <button onClick={advance} className="px-8 py-4 bg-teal-500 text-white font-extrabold text-base rounded-2xl cursor-pointer hover:bg-teal-600 whitespace-nowrap">💧 Rửa sạch ống!</button>}
      {step === 1 && <button onClick={advance} className="px-8 py-4 bg-amber-400 text-white font-extrabold text-base rounded-2xl cursor-pointer hover:bg-amber-500 whitespace-nowrap">🧻 Lau khô ống!</button>}

      {step === 3 && (
        <div className="flex flex-col items-center gap-2 w-full max-w-sm">
          <Instruction text="🖱️ Giữ chuột trên ca nước và kéo lên để nghiêng đổ vào ống!" sub="Có vạch xanh chỉ mức nước vừa đủ - đừng đổ quá nhiều!" />
          <div className="w-full h-4 bg-gray-200 rounded-full overflow-hidden mt-1">
            <div className="h-full bg-blue-400 rounded-full transition-all" style={{ width: `${waterLevel}%` }} />
          </div>
          <p className="text-sm text-blue-600 font-bold">Mức nước: {Math.round(waterLevel)}% {waterLevel >= 75 ? '✅ Đủ rồi!' : '— Tiếp tục đổ!'}</p>
        </div>
      )}

      {step === 4 && !leafPlaced && (
        <div onDragOver={e => e.preventDefault()} onDrop={handleLeafDrop}
          className="w-full max-w-sm h-12 rounded-2xl border-2 border-dashed border-green-400 flex items-center justify-center text-sm text-green-600 font-extrabold bg-green-50">
          🍃 Thả lá vào đây (miệng ống nứa)
        </div>
      )}

      {step === 5 && (
        <div className="flex flex-col items-center gap-3 w-full max-w-sm">
          <Instruction text="🖱️ Giữ chuột trên lá và xoay để ép lá bám chặt vào miệng ống!" sub="Lá đặt lệch → cơm có thể trào ra khi nấu!" />
          <div
            onMouseDown={handlePressMouseDown}
            className="w-full h-20 rounded-2xl bg-green-100 border-4 border-green-400 flex items-center justify-center text-4xl cursor-crosshair hover:border-green-600 active:bg-green-200 select-none transition-all">
            🍃 <span className="text-base ml-2">Giữ + xoay ↻ ép lá</span>
          </div>
          <div className="w-full h-4 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${(pressProgress / PRESS_TARGET) * 100}%` }} />
          </div>
          <p className="text-sm text-green-600 font-bold">Ép lá: {Math.round((pressProgress / PRESS_TARGET) * 100)}%</p>
        </div>
      )}

      {step === 6 && (
        <div className="flex flex-col items-center gap-3">
          <Instruction text="👆 Bấm nhiều lần để buộc dây cố định lá không rơi ra!" sub="Bấm liên tục cho đến khi dây buộc chặt" />
          <div className="w-full max-w-sm h-4 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full bg-amber-500 rounded-full transition-all" style={{ width: `${(tieClicks / TIE_TARGET) * 100}%` }} />
          </div>
          <button onClick={handleTieClick}
            className="px-8 py-4 bg-amber-600 text-white font-extrabold text-base rounded-2xl cursor-pointer hover:bg-amber-700 whitespace-nowrap active:scale-95">
            🧵 Bấm buộc dây! ({tieClicks}/{TIE_TARGET})
          </button>
        </div>
      )}

      {isAtAdultStep && !adultDone && (
        <div className="w-full mt-2">
          <p className="text-base font-extrabold text-orange-700 text-center mb-3">Cần khoét lỗ đáy ống! Nhờ người lớn giúp!</p>
          <AdultHelp
            dangerousTask="Khoét lỗ đáy ống bằng dao"
            adultAction="Người lớn đang cẩn thận khoét lỗ nhỏ dưới đáy ống..."
            childSafeTask="Em giữ ống cho chắc, để người lớn khoét!"
            adultEmoji="👨‍🔧"
            onAdultDone={() => {
              setAdultDone(true);
              setDone(true);
              setTimeout(() => onComplete(true, 100), 800);
            }}
          />
        </div>
      )}
      {done && <p className="text-xl font-extrabold text-green-600 text-center">✅ Ống nứa đã sẵn sàng nướng!</p>}
    </div>
  );
}